import {
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {Functie} from '../../../model';
import {ContentService} from '../../../services/content/content.service';
import {FunctieResource} from '../../../services/resources/functie.resource';
import {Router} from '@angular/router';
import {CompareService} from '../../../services/compare/compare.service';
import {CompareEnum} from '../../compare/compare.enum';
import {Competentie, CompetentieCluster} from '../../../model/vergelijking/competentieCluster';
import {ViewportScroller} from '@angular/common';
import {forkJoin, of} from 'rxjs';
import {HtmlContainerComponent} from '../../html-container/html-container.component';
import {PrismicService} from '../../../services/prismic/prismic.service';
import {FunctionDetailPageContent, PrismicFunctionDetailPage} from "../../../../types/app-types";
import {catchError, switchMap} from 'rxjs/operators'
import {Meta, Title} from '@angular/platform-browser'
import {truncateString} from '../../../helpers/stringutil'


const content = require('./functie-detail.content.json');

@Component({
  selector: 'app-functie-detail',
  templateUrl: './functie-detail.component.html',
  styleUrls: ['./functie-detail.component.scss']
})
export class FunctieDetailComponent implements OnChanges {
  @Input()
  public functieSlug: string;

  @Input()
  public mode: string = 'default';

  @Input()
  public showExtendedDetails: boolean = true;

  @Output()
  public cancel: EventEmitter<void> = new EventEmitter();

  @Output()
  public select: EventEmitter<Functie> = new EventEmitter();

  public cmsContent: FunctionDetailPageContent;
  public functie?: Functie;
  public verwanteFuncties: Functie[] = [];
  public vervolgFuncties: Functie[] = [];
  public opleidingen: any = {};

  public selectedCluster: any[];
  public highlighted: string = '';
  public view: string = 'kompas';
  public competentieClusters: any[] = [];
  public opleidingsniveaus: string = '';

  public compareEnum = CompareEnum;

  public windowScrolled: boolean;

  public interests: any[] = [];
  public interestFilters: string[] = ['soort werk', 'doelgroep', 'werkomstandigheden'];

  @ViewChild('arbeidsmarktinformatie', { read: ViewContainerRef })
  public UNSAFE_HTML: ViewContainerRef;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (window.scrollY > 1500 || document.documentElement.scrollTop > 1500 || document.body.scrollTop > 1500) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.scrollY) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  public constructor(
    private contentService: ContentService,
    private functieResource: FunctieResource,
    private router: Router,
    private compareService: CompareService,
    private viewportScroller: ViewportScroller,
    private componentFactoryResolver: ComponentFactoryResolver,
    private prismic: PrismicService,
    private titleService: Title,
    private metaService: Meta
  ) {
    contentService.loadContent(content);
    this.prismic.getDocumentByUid<PrismicFunctionDetailPage>('function_detail_page', 'function_detail_page').then(doc => {
      this.cmsContent = this.transform(doc.data);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.functieSlug && changes.functieSlug.previousValue !== changes.functieSlug.currentValue) {
      this.fetchFunctieData();
    }
  }

  public fetchFunctieData(): void {
    this.functieResource.getFunctie(this.functieSlug).pipe(
      switchMap((functieDetails) => {
        this.functie = functieDetails;
        this.titleService.setTitle(`${functieDetails.naam} | Zorginspirator`)
        this.metaService.updateTag({name: 'description', content: truncateString(functieDetails.omschrijving, 160)}, `name='description'`)
        this.metaService.updateTag({name: 'title', content: `${functieDetails.naam} | Zorginspirator`}, `name='title'`)
        this.metaService.updateTag({name: 'keywords', content: ['functie', functieDetails.naam.toLowerCase().split(' ').filter(val => !['de', 'het', 'een', 'en', '/'].includes(val))].join(', ').replace(/[()]/g, '')}, `name='keywords'`)

        // Chain subsequent API calls using forkJoin
        return forkJoin([
          of(functieDetails), // Emit functieDetails from getFunctie() as part of forkJoin
          this.functieResource.getVerwanteFuncties(functieDetails.id.toString(), true),
          this.functieResource.getVervolgFuncties(functieDetails.id.toString()),
          this.functieResource.getOpleidingForFunction(functieDetails.id.toString())
        ]);
      }),
      catchError((error) => {
        if (error.status === 404) {
          this.router.navigate(['/404']);
        }
        return of(null); // Return observable to handle error
      })
    ).subscribe(
      ([functieDetails, verwanteFuncties, vervolgFuncties, opleidingen]) => {
        // Assign values to component properties after all API calls complete
        this.functie = functieDetails;
        this.verwanteFuncties = verwanteFuncties;
        this.vervolgFuncties = vervolgFuncties;
        this.opleidingen = opleidingen;

        // Additional logic that depends on the fetched data
        this.opleidingsniveaus = this.functie.opleidingsniveaus.join(', ');
        this.competentieClusters = this.createRadarChartDataObject(this.functie.competentieClusters);

        this.interests = this.interestFilters.map(filter => this.filterInterests(filter));

        setTimeout(() => {
          if (this.functie.arbeidsmarktPerspectief && this.mode !== 'selector') {
            // Dynamically load HTML content (handle with caution)
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(HtmlContainerComponent);
            this.UNSAFE_HTML.clear();
            this.UNSAFE_HTML.createComponent(componentFactory).instance.htmlAsString = this.functie.arbeidsmarktPerspectief;
          }
        });

        this.scrollToTop();

        setTimeout(() => {
          // Scroll to anchor based on router fragment
          this.router.routerState.root.fragment.subscribe((currentHash) =>
            currentHash && this.viewportScroller.scrollToAnchor(currentHash));
        });
      }
    );
  }

  public back(): void {
    this.mode === 'default' ? window.history.back() : this.cancel.emit();
  }

  public handleGotoVacancySite(): void {
    window.open(this.functie.linkVacatures, this.cmsContent.naarVacatureSiteCtaTargetBlank ? "_blank" : "_self");
  }

  public handleSelect(): void {
    this.select.emit(this.functie);
  }

  public scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  public compareWithToekomstFunctie(): void {
    this.functieResource.getFunctie(this.functie.toekomstfunctieId).subscribe(functie => {
      this.compareService.addItemToCompare(this.functie);
      this.compareService.addItemToCompare(functie);
      this.router.navigate(['/compare']);
    });
  }

  public createRadarChartDataObject(data: CompetentieCluster[]): any[] {
    return data.map(item => ({
      item: item.naam,
      valueLeft: item.gewicht,
      open: false,
      competentiesLeft: item.competenties.slice().sort((a: Competentie, b: Competentie) => a.id - b.id)
    }));
  }

  public handleAxisSelect($event: any): void {
    this.highlighted = $event;
    this.selectedCluster = $event;
  }

  public scrollToTopOfPage(): any {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }

  public toggleCompetentieCluster(cluster: any): void {
    cluster.open = !cluster.open;
  }

  public loadFunctie(event: any): void {
    this.functieSlug = event.slug;
    this.router.navigate([`/functie/${this.functieSlug}`]);
  }

  public filterInterests(type: string) {
    return this.functie.voorkeuren.filter(voorkeur => voorkeur.soort === type).map(voorkeur => {
      return {
        name: voorkeur.naam,
        type: type.charAt(0).toUpperCase() + type.slice(1),
        selected: true
      };
    });
  }

  private transform(doc: PrismicFunctionDetailPage): FunctionDetailPageContent {
    return {
      waaromPastFunctieTitle: this.prismic.convertToText(doc.waarom_past_functie_title),
      watDoeJeInFunctieTitle: this.prismic.convertToText(doc.wat_doe_je_in_functie_title),
      voorkeurenTitle: this.prismic.convertToText(doc.voorkeuren_title),
      voorkeurenDescription: this.prismic.convertToHtml(doc.voorkeuren_description),
      toekomstVanFunctieTitle: this.prismic.convertToText(doc.toekomst_van_functie_title),
      toekomstVanFunctieSubtitle: this.prismic.convertToHtml(doc.toekomst_functie_subtitle),
      kennisVaardighedenTitle: this.prismic.convertToText(doc.kennis_vaardigheden_title),
      kennisVaardighedenDescription: this.prismic.convertToHtml(doc.kennis_vaardigheden_description),
      arbeidsmarktTitle: this.prismic.convertToText(doc.arbeidsmarkt_title),
      beroepsOnderewijsLaneTitle: this.prismic.convertToText(doc.beroepsonderwijs_lane_title),
      verwanteFunctiesLaneTitle: this.prismic.convertToText(doc.verwantefuncties_lane_title),
      vervolgFunctiesLaneTitle: this.prismic.convertToText(doc.vervolgfuncties_lane_title),
      naarVacatureSiteCtaLabel: this.prismic.convertToText(doc.naar_vacature_site_cta_label),
      naarVacatureSiteCtaTargetBlank: doc.naar_vacature_site_cta_target_blank
    };
  }
}

